// @spec AUD-STR-023
'use client';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { AttestationConfirmation, AttestationForm, useDownloadPdf } from '@/features/evidence';
import type { AttestationResponse } from '@/features/evidence';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/evidence/attestations/new')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.createAttestation'),
  }),
  component: NewAttestationPage,
});

/**
 * Create new attestation page.
 *
 * Multi-step flow:
 * 1. Statement input with compliance domain/requirement
 * 2. Preview the attestation
 * 3. Confirm and sign
 * 4. Success confirmation with PDF download
 */
function NewAttestationPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);
  const [signedAttestation, setSignedAttestation] = useState<AttestationResponse | null>(null);

  const { downloadPdf, isDownloading } = useDownloadPdf({
    onError: (error) => {
      console.error('PDF download failed:', error);
    },
  });

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-muted" />
          <div className="h-64 w-96 rounded bg-muted" />
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="rounded-lg border border-dashed p-12 text-center">
          <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
          <p className="mt-2 text-muted-foreground">
            {t('common:routeErrors.noOrgSelectedDesc')}
          </p>
          <Button variant="outline" asChild className="mt-4">
            <Link to="/settings/organizations">{t('common:routeErrors.goToOrganizations')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Show confirmation after successful signing
  if (signedAttestation) {
    return (
      <div className="container max-w-4xl py-8">
        <AttestationConfirmation
          attestation={signedAttestation}
          isDownloading={isDownloading}
          onDownloadPdf={() => {
            void downloadPdf(signedAttestation.id);
          }}
          onViewDetails={() => {
            // Navigate to attestation detail (to be implemented in future story)
            void navigate({ to: '/evidence/attestations/new' });
          }}
          onCreateAnother={() => {
            setSignedAttestation(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t('common:routeErrors.createAttestation')}</h1>
        <p className="text-muted-foreground">
          Create a signed declaration confirming compliance status.
        </p>
      </div>

      <AttestationForm
        onSuccess={(attestation) => {
          setSignedAttestation(attestation);
        }}
        onCancel={() => {
          void navigate({ to: '/dashboard' });
        }}
      />
    </div>
  );
}
